import React from "react";

import { AppConfig } from "src/config/AppConfig";
import { showToast } from "src/app/methods/showToast";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { SelectedCreator } from "src/app/modals/AddCreatorsCDTWorkspaceProjectModal/AddCreatorsCDTWorkspaceProjectModal";
import { UniversalSettingsTab } from "src/app/SettingsModal/SettingsModal";
import { ErrorMessage, parseErrorMessages } from "src/utils/methods";
import { SubscriptionEntitlements } from "src/app/subscription/subscriptionUtils";
import { WelcomePageData } from "src/app/Welcome/sections/WelcomeQuickStart/WelcomeQuickStart";
import { Identity } from "src/types";
import { Team } from "src/app/SettingsModal/tabs/Teams/utils";
import * as actionTypes from "./mainTypes";
import { AnalyzedBrief } from "src/types/brief";

export type HowItWorksModalDataProps = {
  location: AppLocation;
};

export type CreateNewProjectData = {
  selectedCreatorsPayload: SelectedCreator[];
  taskType?: string;
} | null;

export enum AppLocation {
  CreateNewProject = "create-new-project",
  PromoCodes = "promo-codes",
  Navbar = "navbar",
  TaskMetaFields = "task-meta-fields",
  Task = "task",
  ProjectList = "project-list",
  SingleProject = "single-project",
  TopbarRight = "topbar-right",
  ProjectOverviewTable = "project-overview-table",
  SettingsModal = "settings-modal",
  ProjectDetails = "project-details",
  Empty = "",
}

export type SettingsModalDataProps = {
  location: AppLocation;
  context: string;
  tab: UniversalSettingsTab;
  section?: string;
  objectUuid?: string;
};

export interface Workspace {
  openMeterPlan?: string | null;
  openMeterManaged?: boolean;
  workspaceFlags?: { internal: boolean; test: boolean; sandbox: boolean };
  name: string;
  uuid: string;
  wsUrl: string;
}

export type NotificationType = "reload" | "online" | "offline";

interface Notification {
  id: string;
  type: NotificationType;
  message: string | undefined;
}

export type WsProjectReportShowcasePdfReadyModalData = {
  reportUrl: string;
  fileName: string;
};

export enum SubscriptionEntitlementErrorTypes {
  EntitlementAccessNotGranted = "EntitlementAccessNotGranted",
  EntitlementUsageExceeded = "EntitlementUsageExceed",
}

export interface SubscriptionEntitlementError {
  field: SubscriptionEntitlements;
  message: string;
  type: SubscriptionEntitlementErrorTypes;
}

export interface NewProjectBriefData extends Partial<AnalyzedBrief> {
  name: string;
  key: string;
}

export interface MainReducerState {
  translations: any;
  locale: string;
  supportedLanguages: string[];
  identity: Partial<Identity>;
  identifyLoading: boolean;
  notifications: Notification[];
  wsProjectReportShowcasePdfReadyModalData: WsProjectReportShowcasePdfReadyModalData | null;
  activeWorkspaceUuid: string;
  workspacesList: Workspace[];
  creatorDiscoveryToolUrl: string | null;
  showAppMessageModal: boolean;
  chatwootUnreadMessages: number;
  settingsModalData: SettingsModalDataProps;
  howItWorksModalData: HowItWorksModalDataProps;
  createNewProjectData: CreateNewProjectData;
  websocket: WebSocket | null;
  identifyError: string | null;
  errorMessages: ErrorMessage[];
  subscriptionEntitlementErrors: SubscriptionEntitlementError[];
  workspaceListDropdownVisible: boolean;
  welcomePageData: WelcomePageData | null;
  isWelcomePageDataLoading: boolean;
  teams: Team[];
  isOpenAddBriefPaneForNewProject: boolean;
  isNewProjectBriefLoading: boolean;
  newProjectBriefData: NewProjectBriefData | null;
  showTableSearch: boolean;
  isWorkspacesListLoading: boolean;
}

export const initialSettingsModalData = {
  location: AppLocation.Empty,
  tab: "",
  context: "",
  section: "",
};

export const initialHowItWorksModalData = { location: AppLocation.Empty };

const initialState: MainReducerState = {
  translations: {},
  locale: "en_GB",
  supportedLanguages: ["en_GB"],
  identity: {},
  identifyLoading: true,
  activeWorkspaceUuid: "",
  workspacesList: [],
  creatorDiscoveryToolUrl: null,
  notifications: [],
  wsProjectReportShowcasePdfReadyModalData: null,
  showAppMessageModal: false,
  chatwootUnreadMessages: 0,
  settingsModalData: initialSettingsModalData,
  howItWorksModalData: initialHowItWorksModalData,
  createNewProjectData: null,
  websocket: null,
  identifyError: null,
  errorMessages: [],
  subscriptionEntitlementErrors: [],
  workspaceListDropdownVisible: false,
  welcomePageData: null,
  isWelcomePageDataLoading: false,
  teams: [],
  isOpenAddBriefPaneForNewProject: false,
  isNewProjectBriefLoading: false,
  newProjectBriefData: null,
  showTableSearch: false,
  isWorkspacesListLoading: false,
};

// eslint-disable-next-line @typescript-eslint/default-param-last
function mainReducer(state = initialState, action: any): MainReducerState {
  switch (action.type) {
    case actionTypes.GET_TRANSLATIONS_SUCCESS: {
      const { languages } = action.payload.data.content;

      return {
        ...state,
        translations: action.payload.data.content?.trans,
        locale: action.payload.data.content?.locale,
        supportedLanguages: languages?.length
          ? languages
          : state.supportedLanguages,
      };
    }

    case actionTypes.GET_IDENTIFY_SUCCESS:
    case actionTypes.GET_IS_LOGGED_IN_SUCCESS: {
      // Checking if endpoint included workspaceUuid parameter, if so, correct permissions should have been downloaded
      const permissionsLoaded =
        action.payload.config.url.includes("workspaceUuid");

      return {
        ...state,
        identity: action.payload.data?.content,
        identifyLoading: !permissionsLoaded,
        identifyError: null,
      };
    }

    case actionTypes.GET_IDENTIFY_FAIL:
      if (
        !action?.error?.config?.reduxSourceAction?.payload?.request?.url?.includes(
          "=trial",
        )
      ) {
        showToast(
          "info",
          <IDHFormattedMessage id="ws_identity_title" defaultMessage="Info" />,
          <IDHFormattedMessage
            id="ws_identity_please_log_in"
            defaultMessage="Please log in"
          />,
        );
      }

      return {
        ...state,
        identifyError: actionTypes.GET_IDENTIFY_FAIL,
      };

    case actionTypes.GET_IS_LOGGED_IN_FAIL:
      return {
        ...state,
        identifyError: actionTypes.GET_IDENTIFY_FAIL,
      };

    case actionTypes.SET_ACTIVE_WORKSPACE_UUID:
      return {
        ...state,
        activeWorkspaceUuid: action.payload.workspaceUuid,
      };

    case actionTypes.SET_CHATWOOT_UNREAD_MESSAGES:
      return {
        ...state,
        chatwootUnreadMessages: action.payload.counter,
      };

    case actionTypes.SET_WEBSOCKET:
      return {
        ...state,
        websocket: new WebSocket(
          `${AppConfig.getWebsocketAddress()}/workspace/connect?workspaceId=${action.payload.workspaceUuid}&userId=${action.payload.userId}&challenge=${action.payload.challenge}`,
        ),
      };

    case actionTypes.CLEAR_WEBSOCKET:
      return {
        ...state,
        websocket: null,
      };

    case actionTypes.GET_WORKSPACES_LIST: {
      return {
        ...state,
        isWorkspacesListLoading: true,
      };
    }

    case actionTypes.GET_WORKSPACES_LIST_SUCCESS:
      return {
        ...state,
        workspacesList: action.payload.data.content,
        isWorkspacesListLoading: false,
      };

    case actionTypes.GET_WORKSPACES_LIST_FAIL:
      return {
        ...state,
        isWorkspacesListLoading: false,
      };

    case actionTypes.GET_CREATOR_DISCOVERY_TOOL_URL_SUCCESS:
      return {
        ...state,
        creatorDiscoveryToolUrl:
          action.payload.data.content.creatorDiscoveryToolUrl,
      };

    case actionTypes.SET_PDF_READY_MODAL_DATA:
      return {
        ...state,
        wsProjectReportShowcasePdfReadyModalData: action.payload.data,
      };

    case actionTypes.SEND_NOTIFICATION:
      return {
        ...state,
        notifications: [
          ...state.notifications.filter((n) => n.type !== action.payload.type),
          action.payload,
        ],
      };

    case actionTypes.DROP_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(
          (n) => n.id !== action.payload.id,
        ),
      };

    case actionTypes.SET_SETTINGS_MODAL_DATA:
      return {
        ...state,
        settingsModalData: action.payload.settingsModalData,
      };

    case actionTypes.SET_HOW_IT_WORKS_MODAL_DATA:
      return {
        ...state,
        howItWorksModalData: action.payload.howItWorksModalData,
      };

    case actionTypes.SET_CREATE_NEW_PROJECT_DATA:
      return {
        ...state,
        createNewProjectData: action.payload.createNewProjectData,
      };

    case actionTypes.CLEAR_CREATE_NEW_PROJECT_DATA:
      return {
        ...state,
        createNewProjectData: null,
      };

    case actionTypes.SET_SUBSCRIPTION_ENTITLEMENT_ERRORS:
      return {
        ...state,
        subscriptionEntitlementErrors:
          action.payload.subscriptionEntitlementErrors,
      };

    case actionTypes.SET_WORKSPACE_LIST_DROPDOWN_VISIBLE:
      return {
        ...state,
        workspaceListDropdownVisible: action.payload.visible,
      };

    case actionTypes.GET_WELCOME_PAGE_DATA:
      return {
        ...state,
        isWelcomePageDataLoading: true,
      };

    case actionTypes.GET_WELCOME_PAGE_DATA_SUCCESS:
      return {
        ...state,
        welcomePageData: action.payload.data?.content,
        isWelcomePageDataLoading: false,
      };

    case actionTypes.GET_WELCOME_PAGE_DATA_FAIL:
      return {
        ...state,
        isWelcomePageDataLoading: false,
      };

    case actionTypes.GET_TEAMS_FOR_WORKSPACE_SUCCESS:
      return {
        ...state,
        teams: action.payload.data?.content,
      };

    case actionTypes.SET_OPEN_ADD_BRIEF_PANE_FOR_NEW_PROJECT:
      return {
        ...state,
        isOpenAddBriefPaneForNewProject: action.payload.isOpen,
      };

    case actionTypes.SET_LOADING_BRIEF_FOR_NEW_PROJECT:
      return {
        ...state,
        isNewProjectBriefLoading: action.payload.isLoading,
      };

    case actionTypes.SET_BRIEF_DATA_FOR_NEW_PROJECT:
      return {
        ...state,
        newProjectBriefData: action.payload.data,
      };

    case actionTypes.UPDATE_SHOW_TABLE_SEARCH:
      const { showTableSearch } = action.payload;
      return {
        ...state,
        showTableSearch:
          typeof showTableSearch === "boolean"
            ? showTableSearch
            : !state.showTableSearch,
      };

    default: {
      if (action?.error?.response?.data && action.type.includes("_FAIL")) {
        if (
          action.error.response.data?.statusCode === 402 ||
          (action.error.response.data?.errors &&
            action.error.response.data?.errors.length > 0 &&
            action.error.response.data?.errors[0]?.field ===
              "User is not member specified project")
        ) {
          // TODO: tmp hack, https://app.asana.com/0/0/1203142742951466/f
          return state;
        }

        return {
          ...state,
          errorMessages: parseErrorMessages(
            action?.error?.response?.data?.errors,
          ),
        };
      }

      if (
        action?.payload?.data?.status === "fail" ||
        action?.error?.response?.status >= 500
      ) {
        return {
          ...state,
          errorMessages: [{ message: "An unexpected error occurred." }],
        };
      }
      return state;
    }
  }
}

export default mainReducer;
